import { html, LitElement, nothing } from 'lit';
import { styles } from '../view/styles';
import { translateFn } from '../../../../../utils/translator.helper';
import { customElement, state } from 'lit/decorators.js';
import { ValidDeleteStates } from '../types';
import { DeleteActionState } from '../../../../networkAppContainer/helpers/types';
import { FontSizes } from '../../../../../types/common/types';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

@customElement('gz-delete-entity-web-component')
export class GzDeleteEntityWebComponent extends LitElement {
  @state()
  public deletePopUpCases: ValidDeleteStates;

  static get styles() {
    return styles;
  }

  protected render() {
    if (this.deletePopUpCases[DeleteActionState.AD_ENTITIES_IN_SELECTED_ITEMS]) {
      return html`${this.renderActiveDirectorySection()}`;
    }

    if (this.deletePopUpCases[DeleteActionState.AWS_ENTITIES_IN_SELECTED_ITEMS]) {
      return html`${this.renderAwsSection()}`;
    }

    return html`
      ${this.renderPopulatedComputerFolderSection()} ${this.renderEmptyFoldersSections()}
      ${this.renderManagedEndpointsSection()} ${this.renderUnmanagedEndpointsSection()}
      ${this.renderSvaInSelectedItemsSection()} ${this.renderContainerHostDeletableSection()}
      ${this.renderEndpointsInExcludedFolderSection()}
    `;
  }

  private renderPopulatedComputerFolderSection() {
    const gzTextContent = `<gz-text fontSize="${FontSizes.HEADING_6}">
        ${translateFn(
          'Populated folders will be permanently deleted, and their contents will be moved to the {OPEN_TAG} {folder_name} {CLOSE_TAG} folder.',
          {
            OPEN_TAG: '<b>',
            CLOSE_TAG: '</b>',
            folder_name: Translator.translateProtectedEntityType(Constants.ProtectedEntities.EPS.TYPE_EXCLUDED_FOLDER),
          }
        )}
      </gz-text>`;

    return this.deletePopUpCases[DeleteActionState.NOT_EMPTY_CUSTOM_GROUPS_FOLDER_IN_SELECTED_ITEMS]
      ? html`${unsafeHTML(gzTextContent)}`
      : nothing;
  }

  private renderEmptyFoldersSections() {
    return this.deletePopUpCases[DeleteActionState.EMPTY_CUSTOM_GROUPS_FOLDER_IN_SELECTED_ITEMS] ||
      this.deletePopUpCases[DeleteActionState.EMPTY_COMPANY_FOLDER_IN_SELECTED_ITEMS] ||
      this.deletePopUpCases[DeleteActionState.EMPTY_CONTAINER_GROUP_IN_SELECTED_ITEMS]
      ? html` <gz-text fontSize="${FontSizes.HEADING_6}">
          ${translateFn('Empty folders will be permanently deleted.')}
        </gz-text>`
      : nothing;
  }

  private renderManagedEndpointsSection() {
    const gzTextContent = `<gz-text fontSize="${FontSizes.HEADING_6}">
        ${translateFn(
          'Managed endpoints will be moved to the {OPEN_TAG} {folder_name} {CLOSE_TAG} folder. Additionally, the security agent will automatically be uninstalled.',
          {
            OPEN_TAG: '<b>',
            CLOSE_TAG: '</b>',
            folder_name: Translator.translateProtectedEntityType(Constants.ProtectedEntities.EPS.TYPE_EXCLUDED_FOLDER),
          }
        )}
      </gz-text>`;
    return this.deletePopUpCases[DeleteActionState.MANAGED_ENDPOINTS_IN_SELECTED_ITEMS] &&
      !this.deletePopUpCases[DeleteActionState.MANAGED_ENDPOINTS_ONLY_IN_EXCLUDED_FOLDER]
      ? html`${unsafeHTML(gzTextContent)}`
      : nothing;
  }

  private renderUnmanagedEndpointsSection() {
    const gzTextContent = `<gz-text fontSize="${FontSizes.HEADING_6}">
        ${translateFn('Unmanaged endpoints will be moved to the {OPEN_TAG} {folder_name} {CLOSE_TAG} folder.', {
          OPEN_TAG: '<b>',
          CLOSE_TAG: '</b>',
          folder_name: Translator.translateProtectedEntityType(Constants.ProtectedEntities.EPS.TYPE_EXCLUDED_FOLDER),
        })}
      </gz-text>`;
    return this.deletePopUpCases[DeleteActionState.UNMANAGED_ENDPOINTS_IN_SELECTED_ITEMS] &&
      !this.deletePopUpCases[DeleteActionState.UNMANAGED_ENDPOINTS_ONLY_IN_EXCLUDED_FOLDER]
      ? html`${unsafeHTML(gzTextContent)}`
      : nothing;
  }

  private renderSvaInSelectedItemsSection() {
    const gzTextContent = `<gz-text fontSize="${FontSizes.HEADING_6}">
        ${translateFn(
          'The selected Security Servers will be moved to the {OPEN_TAG} {folder_name} {CLOSE_TAG} folder.',
          {
            OPEN_TAG: '<b>',
            CLOSE_TAG: '</b>',
            folder_name: Translator.translateProtectedEntityType(Constants.ProtectedEntities.EPS.TYPE_EXCLUDED_FOLDER),
          }
        )}
      </gz-text>`;

    return this.deletePopUpCases[DeleteActionState.SVA_IN_SELECTED_ITEMS] &&
      !this.deletePopUpCases[DeleteActionState.SVA_ONLY_IN_EXCLUDED_FOLDER]
      ? html`${unsafeHTML(gzTextContent)}`
      : nothing;
  }

  private renderContainerHostDeletableSection() {
    const gzTextContent = `<gz-text fontSize="${FontSizes.HEADING_6}">
        ${translateFn(
          'The folders of the container hosts will be permanently deleted. The virtual machines will be moved to the {OPEN_TAG} {folder_name} {CLOSE_TAG} folder, while their containers will be deleted.',
          {
            OPEN_TAG: '<b>',
            CLOSE_TAG: '</b>',
            folder_name: Translator.translateProtectedEntityType(Constants.ProtectedEntities.EPS.TYPE_EXCLUDED_FOLDER),
          }
        )}
      </gz-text>`;

    return this.deletePopUpCases[DeleteActionState.CONTAINER_HOST_VM_DELETABLE_IN_SELECTED_ITEMS]
      ? html`${unsafeHTML(gzTextContent)}`
      : nothing;
  }

  private renderEndpointsInExcludedFolderSection() {
    const gzTextContent = `<gz-text fontSize="${FontSizes.HEADING_6}">
        ${translateFn(
          'Entities already in the {OPEN_TAG} {folder_name} {CLOSE_TAG} folder will be permanently deleted.',
          {
            OPEN_TAG: '<b>',
            CLOSE_TAG: '</b>',
            folder_name: Translator.translateProtectedEntityType(Constants.ProtectedEntities.EPS.TYPE_EXCLUDED_FOLDER),
          }
        )}
      </gz-text>`;

    return this.deletePopUpCases[DeleteActionState.ENDPOINTS_IN_EXCLUDED_FOLDER]
      ? html`${unsafeHTML(gzTextContent)}`
      : nothing;
  }

  private renderActiveDirectorySection() {
    const gzTextContent = `<gz-text fontSize="${FontSizes.HEADING_6}">
        ${translateFn(
          'Integration endpoints will become unlicensed, and the security agent will be uninstalled. Integration folders and endpoints will keep their current location.'
        )}
      </gz-text>`;

    return this.deletePopUpCases[DeleteActionState.AD_ENTITIES_IN_SELECTED_ITEMS]
      ? html`${unsafeHTML(gzTextContent)}`
      : nothing;
  }

  private renderAwsSection() {
    const gzTextContent = `<gz-text fontSize="${FontSizes.HEADING_6}">
        ${translateFn('Terminated EC2 instances will be removed from the inventory.')}
      </gz-text>`;

    return this.deletePopUpCases[DeleteActionState.AWS_ENTITIES_IN_SELECTED_ITEMS]
      ? html`${unsafeHTML(gzTextContent)}`
      : nothing;
  }
}
